//  ----------------------------------------------------------------------------
//  Dependencies
//  ----------------------------------------------------------------------------
import { Button, IconButton, Modal, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Skeleton, Tooltip } from '@mui/material';

import { ArrowRight, Close, Visibility, Settings, CheckCircle } from '@mui/icons-material';
import { Col, Row } from 'antd';
import moment from 'moment';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
//  -- Constants ----------------------
import PasswordRevealInline from 'components/PasswordRevealInline';
import COLOR from 'utils/constants/color';
//  -- Components ---------------------
import Loading from 'components/Loading';
import LoadingWrapper from 'components/LoadingWrapper';
import { UserInfoCard, ModalContent, ModalSection, Accordion, AccordionSummary, AccordionDetails, AccordianTitle, Rotate, SearchForm } from 'components/UserView';
//  -- Thunks -------------------------
import { get, isEmpty, map } from 'lodash';
import { lighten } from 'polished';
import { handleUserByCanvasName  , handleGetUserInfoById, handleGetCanvasCoursesByWorkflowState } from 'store/canvas/thunks';
import { createKeystore } from "store/keystore/thunks";
import { v4 } from 'uuid';
import { useNavigate } from "react-router-dom";
import { getUserIntegration } from 'utils';
import SentryWrap from 'components/Sentry';
//  ----------------------------------------------------------------------------
//  Styles
//  ----------------------------------------------------------------------------
const Wrapper = styled.div`
	height: 100%;
	width: 100%;
`;

const WrapperForm = styled.form`
	padding: 40px 0px;
	width: 100%;
`;
const Error = styled.div`
    background-color: ${lighten(0.4, 'red')};
    border: 1px solid red;
    border-radius: 3px;
    color: red;
    font-size: 14px;
    font-weight: 500;
    margin: 0px auto 16px;
    max-width: 480px;
    padding: 8px;
    text-align: center;
    width: 100%;
`;
const Label = styled.div`
   font-size:14px;
   color:${COLOR.white};
`;
const LabelTop = styled.div`
   font-size:18px;
   color:${COLOR.white};
`;
const ItemsTabsList = styled.div`
    display: inline-block;
    flex-direction: column;
    min-height: 100px;
    border-radius: 12px;
    min-width: 200px;
    background-color: ${COLOR.indigodeep};
    width: 90%;
    padding: 16px;
    margin-top: 10px;
    min-height: 220px;
`;
const ClassUsersWrapper = styled.div`
    border: 1px dashed ${COLOR.black700};
    border-radius: 3px;
    margin-bottom: 8px;
    width:102%;
    &:last-child {
        margin-bottom: 0px;
    }
`;
const ContentArea = styled.div`
width: 90%;
margin-top: 28px;
`;
//  ----------------------------------------------------------------------------
//  Container
//  ----------------------------------------------------------------------------
    const UserInfoAres = ({ data }) => {
        return <UserInfoCard data={data} userPath="user" schoolPath="school" districtPath="district" />
    }
const CourseInfoArea = ({ courses, loading, title, workflowState }) => {
    if (loading) {
        return (
            <ContentArea>
                <div style={{ marginTop: '16px' }}>
                    {[1, 2, 3].map((i) => (
                        <div key={i} style={{ marginBottom: '16px' }}>
                            <Skeleton variant="rectangular" height={60} />
                        </div>
                    ))}
                </div>
            </ContentArea>
        );
    }

    const getStatusColor = (state) => {
        switch (state) {
            case 'unpublished': return '#faad14'; // Light yellow/amber
            case 'completed': return '#52c41a'; // Green
            case 'deleted': return '#ff4d4f'; // Red
            default: return '#1890ff'; // Blue
        }
    };

    const getStatusText = (state) => {
        switch (state) {
            case 'unpublished': return 'Unpublished';
            case 'completed': return 'Completed';
            case 'deleted': return 'Deleted';
            default: return 'Active';
        }
    };
    
    return (
        <div style={{ padding: '20px' }}>
            {courses && courses.length > 0 ? (
                <div>
                    {map(courses, (course, index) => {
                        // Build description parts similar to CourseSync
                        const descriptionParts = [];
                        
                        if (get(course, 'course_info.course_code')) {
                            descriptionParts.push(`Course Code: ${get(course, 'course_info.course_code')}`);
                        }
                        if (get(course, 'course_info.time_zone')) {
                            descriptionParts.push(`Time Zone: ${get(course, 'course_info.time_zone')}`);
                        }
                        if (course.created_at) {
                            descriptionParts.push(`Created: ${moment(course.created_at).format('MMM D, YYYY')}`);
                        }
                        if (get(course, 'course_info.start_at')) {
                            descriptionParts.push(`Start: ${moment(get(course, 'course_info.start_at')).format('MMM D, YYYY')}`);
                        }
                        if (get(course, 'course_info.end_at')) {
                            descriptionParts.push(`End: ${moment(get(course, 'course_info.end_at')).format('MMM D, YYYY')}`);
                        }

                        const metaDescription = descriptionParts.length > 0 ? descriptionParts.join(' | ') : undefined;
                        const statusTag = (
                            <span style={{ 
                                color: getStatusColor(workflowState), 
                                marginLeft: 6,
                                fontWeight: '500'
                            }}>
                                ({getStatusText(workflowState)})
                            </span>
                        );

                        return (
                            <div key={course.id || index} style={{ 
                                border: '1px solid #e9ecef',
                                borderRadius: '12px',
                                marginBottom: '16px',
                                backgroundColor: '#fff',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                transition: 'all 0.3s ease',
                                overflow: 'hidden'
                            }}>
                                <div style={{ 
                                    padding: '20px 24px',
                                    background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)'
                                }}>
                                    <div style={{ 
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        marginBottom: '12px'
                                    }}>
                                        <div style={{ 
                                            flex: 1,
                                            textAlign: 'left'
                                        }}>
                                            <div style={{ 
                                                fontSize: '18px',
                                                fontWeight: '700',
                                                color: '#212529',
                                                marginBottom: '6px',
                                                lineHeight: '1.3',
                                                textAlign: 'left'
                                            }}>
                                                {course.name || 'N/A'}
                                                {statusTag}
                                            </div>
                                            <div style={{ 
                                                fontSize: '15px', 
                                                color: '#6c757d',
                                                marginBottom: '8px',
                                                textAlign: 'left',
                                                fontWeight: '500'
                                            }}>
                                                Course Name: {get(course, 'course_info.name', 'N/A')}
                                            </div>
                                        </div>
                                        <div style={{ 
                                            marginLeft: '20px',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}>
                                            <div style={{
                                                background: `linear-gradient(135deg, ${getStatusColor(workflowState)} 0%, ${getStatusColor(workflowState)}dd 100%)`,
                                                color: 'white',
                                                padding: '8px 16px',
                                                borderRadius: '20px',
                                                fontSize: '12px',
                                                fontWeight: '700',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.8px',
                                                boxShadow: `0 4px 8px ${getStatusColor(workflowState)}40`,
                                                border: `2px solid ${getStatusColor(workflowState)}20`
                                            }}>
                                                {getStatusText(workflowState)}
                                            </div>
                                        </div>
                                    </div>
                                    {metaDescription && (
                                        <div style={{ 
                                            fontSize: '14px', 
                                            color: '#6c757d',
                                            lineHeight: '1.6',
                                            textAlign: 'left',
                                            background: 'rgba(108, 117, 125, 0.05)',
                                            padding: '12px 16px',
                                            borderRadius: '8px',
                                            border: '1px solid rgba(108, 117, 125, 0.1)'
                                        }}>
                                            {metaDescription}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div style={{ 
                    textAlign: 'center', 
                    color: '#6c757d',
                    fontSize: '16px',
                    padding: '60px 20px',
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    borderRadius: '12px',
                    border: '2px dashed #dee2e6',
                    margin: '20px 0'
                }}>
                    <div style={{ 
                        fontSize: '48px', 
                        marginBottom: '16px',
                        opacity: 0.5
                    }}>
                        📚
                    </div>
                    <div style={{ 
                        fontWeight: '600',
                        marginBottom: '8px'
                    }}>
                        No courses found
                    </div>
                    <div style={{ 
                        fontSize: '14px',
                        opacity: 0.7
                    }}>
                        No {workflowState} courses available at the moment
                    </div>
                </div>
            )}
        </div>
    );
};

const ClassInfoArea = ({ handleChange, data, classEpendOwe,canvasInfo }) => {
    const { classes } = data;
    const { classes: canvasClasses } = canvasInfo;
    const sortedClasses = Array.isArray(classes) ? classes.sort((a, b) => {
        return a.canvas_id - b.canvas_id;
    }) : [];
    const sortedClassesCanvas = Array.isArray(canvasClasses) ? canvasClasses.sort((a, b) => {
        // Get canvas_id values from sortedClasses for matching (text field)
        const activeClassIds = sortedClasses.map(cls => cls.canvas_id);
        
        // Convert canvas id (numeric) to string for comparison with canvas_id (text)
        const aIdString = String(a.id);
        const bIdString = String(b.id);
        
        // Check if both classes exist in sortedClasses
        const aInActive = activeClassIds.includes(aIdString);
        const bInActive = activeClassIds.includes(bIdString);
        
        // Prioritize those that exist in sortedClasses
        if (aInActive && !bInActive) return -1;
        if (!aInActive && bInActive) return 1;
        
        // If both exist in sortedClasses, maintain the sequence from sortedClasses
        if (aInActive && bInActive) {
            const aIndex = activeClassIds.indexOf(aIdString);
            const bIndex = activeClassIds.indexOf(bIdString);
            return aIndex - bIndex;
        }
        
        // If neither exists in sortedClasses, sort by id (numeric)
        return a.id - b.id;
    }) : [];

    return (<Row style={{width:"100%", marginTop: "32px"}}>
        <Col span={12} style={{paddingRight: "16px"}}>
            <div style={{
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                borderRadius: '12px',
                padding: '16px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                border: '1px solid #dee2e6'
            }}>
                <LabelTop style={{
                    fontSize: '16px',
                    fontWeight: '700',
                    color: '#495057',
                    textAlign: 'center',
                    marginBottom: '16px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                }}>
                    Active Classes
                </LabelTop>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {map(sortedClasses, cls => {
                        return (
                            <div key={cls.canvas_id} style={{
                                background: '#fff',
                                borderRadius: '8px',
                                border: '1px solid #e9ecef',
                                overflow: 'hidden',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                            }}>
                                <Accordion square expanded={classEpendOwe == cls.canvas_id} onChange={handleChange(cls.canvas_id)}>
                                    <AccordionSummary 
                                        aria-controls="panel1d-content" 
                                        id="panel1d-header"
                                        style={{
                                            background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
                                            color: 'white',
                                            minHeight: '40px',
                                            '&:hover': {
                                                background: 'linear-gradient(135deg, #0056b3 0%, #004085 100%)'
                                            }
                                        }}
                                    >
                                        <AccordianTitle style={{ color: 'white', fontWeight: '600', fontSize: '14px' }}>
                                            <span>{cls.displayName} {cls.archived ? <span style={{ color: '#ff6b6b' }}>(Archived)</span> : ''}</span>
                                            <div style={{ display:"flex", alignItems: 'center', gap: '6px' }}>
                                                <span style={{ 
                                                    background: 'rgba(255, 255, 255, 0.2)', 
                                                    padding: '2px 6px', 
                                                    borderRadius: '8px',
                                                    fontSize: '10px',
                                                    fontWeight: '600'
                                                }}>
                                                    Students({get(cls,"classUsers.length",0)})
                                                </span>
                                                <Rotate open={classEpendOwe == cls.canvas_id}>
                                                    <ArrowRight style={{ color: 'white' }} />
                                                </Rotate>
                                            </div>
                                        </AccordianTitle>
                                    </AccordionSummary>
                                    <AccordionDetails style={{ padding: '0' }}>
                                        <div style={{ padding: '12px' }}>
                                            <TableContainer>
                                                <Table style={{ width: "100%", textAlign: "center" }}>
                                                    <TableHead>
                                                        <TableRow style={{ background: '#f8f9fa' }}>
                                                            <TableCell style={{ textAlign: "center", fontWeight: '600', color: '#495057' }}>Id</TableCell>
                                                            <TableCell style={{ textAlign: "center", fontWeight: '600', color: '#495057' }}>Username</TableCell>
                                                            <TableCell style={{ textAlign: "center", fontWeight: '600', color: '#495057' }}>First name</TableCell>
                                                            <TableCell style={{ textAlign: "center", fontWeight: '600', color: '#495057' }}>Last Name</TableCell>
                                                            <TableCell style={{ textAlign: "center", fontWeight: '600', color: '#495057' }}>Canvas ID</TableCell>
                                                            <TableCell style={{ textAlign: "center", fontWeight: '600', color: '#495057' }}>Password</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {map(cls.classUsers, cuser => {
                                                            return (
                                                                <TableRow style={{ '&:hover': { background: '#f8f9fa' } }}>
                                                                    <TableCell style={{ textAlign: "center" }}>
                                                                        <Tooltip title={get(cuser, "id", '')} arrow placement="top">
                                                                            <span style={{ cursor: 'help' }}>
                                                                                {get(cuser, "id", '').length > 5 ? `${get(cuser, "id", '').substring(0, 5)}...` : get(cuser, "id", '')}
                                                                            </span>
                                                                        </Tooltip>
                                                                    </TableCell>
                                                                    <TableCell style={{ textAlign: "center" }}>{get(cuser, "username", '')}</TableCell>
                                                                    <TableCell style={{ textAlign: "center" }}>{get(cuser, "firstName", '')}</TableCell>
                                                                    <TableCell style={{ textAlign: "center" }}>{get(cuser, "lastName", '')}</TableCell>
                                                                    <TableCell style={{ textAlign: "center" }}>{get(cuser, "canvas_id", '')}</TableCell>
                                                                    <TableCell style={{ textAlign: "center" }}><PasswordRevealInline userId={get(cuser, "id")} username={get(cuser, "username")} /></TableCell>
                                                                </TableRow>)
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </div>
                                    </AccordionDetails>
                                </Accordion>
                            </div>
                        )
                    })}
                </div>
            </div>
        </Col>
        <Col span={12} style={{paddingLeft: "16px"}}>
            <div style={{
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                borderRadius: '12px',
                padding: '16px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                border: '1px solid #dee2e6'
            }}>
                <LabelTop style={{
                    fontSize: '16px',
                    fontWeight: '700',
                    color: '#495057',
                    textAlign: 'center',
                    marginBottom: '16px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                }}>
                    Integration Classes
                </LabelTop>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {map(sortedClassesCanvas, cls => {
                        return (
                            <div key={cls.id+"u"} style={{
                                background: '#fff',
                                borderRadius: '8px',
                                border: '1px solid #e9ecef',
                                overflow: 'hidden',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                            }}>
                                <Accordion square expanded={classEpendOwe == cls.id} onChange={handleChange(cls.id)}>
                                    <AccordionSummary 
                                        aria-controls="panel1d-content" 
                                        id="panel1d-header"
                                        style={{
                                            background: 'linear-gradient(135deg, #6f42c1 0%, #5a2d91 100%)',
                                            color: 'white',
                                            minHeight: '40px',
                                            '&:hover': {
                                                background: 'linear-gradient(135deg, #5a2d91 0%, #4c1d7a 100%)'
                                            }
                                        }}
                                    >
                                        <AccordianTitle style={{ color: 'white', fontWeight: '600', fontSize: '14px' }}>
                                            <span>Section: {cls.name} ({get(cls,"course_info.name","")})</span>
                                            <div style={{ display:"flex", alignItems: 'center', gap: '8px' }}>
                                                {/* Check mark if class exists in Active Classes */}
                                                {(() => {
                                                    const activeClassIds = sortedClasses.map(activeCls => activeCls.canvas_id);
                                                    const existsInActive = activeClassIds.includes(String(cls.id));
                                                    return existsInActive ? (
                                                        <CheckCircle style={{ 
                                                            color: '#4caf50', 
                                                            fontSize: '18px',
                                                            background: 'rgba(255, 255, 255, 0.2)',
                                                            borderRadius: '50%',
                                                            padding: '2px'
                                                        }} />
                                                    ) : null;
                                                })()}
                                                <Rotate open={classEpendOwe == cls.id}>
                                                    <ArrowRight style={{ color: 'white' }} />
                                                </Rotate>
                                            </div>
                                        </AccordianTitle>
                                    </AccordionSummary>
                                    <AccordionDetails style={{ padding: '0' }}>
                                        <div style={{ padding: '12px' }}>
                                            <TableContainer>
                                                <Table style={{ width: "100%", textAlign: "center" }}>
                                                    <TableHead>
                                                        <TableRow style={{ background: '#f8f9fa' }}>
                                                            <TableCell style={{ textAlign: "center", fontWeight: '600', color: '#495057' }}>ID</TableCell>
                                                            <TableCell style={{ textAlign: "center", fontWeight: '600', color: '#495057' }}>Name</TableCell>
                                                            <TableCell style={{ textAlign: "center", fontWeight: '600', color: '#495057' }}>Short name</TableCell>
                                                            <TableCell style={{ textAlign: "center", fontWeight: '600', color: '#495057' }}>Login ID</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {map(cls.student, cuser => {
                                                            return (
                                                                <TableRow style={{ '&:hover': { background: '#f8f9fa' } }}>
                                                                    <TableCell style={{ textAlign: "center" }}>
                                                                        <Tooltip title={get(cuser, "user_id", '')} arrow placement="top">
                                                                            <span style={{ cursor: 'help' }}>
                                                                                {get(cuser, "user_id", '').length > 5 ? `${get(cuser, "user_id", '').substring(0, 5)}...` : get(cuser, "user_id", '')}
                                                                            </span>
                                                                        </Tooltip>
                                                                    </TableCell>
                                                                    <TableCell style={{ textAlign: "center" }}>{get(cuser, "user.name", '')}</TableCell>
                                                                    <TableCell style={{ textAlign: "center" }}>{get(cuser, "user.short_name", '')}</TableCell>
                                                                    <TableCell style={{ textAlign: "center" }}>{get(cuser, "user.login_id", '')}</TableCell>
                                                                </TableRow>
                                                            )
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </div>
                                    </AccordionDetails>
                                </Accordion>
                            </div>
                        )
                    })}
                </div>
            </div>
        </Col>
    </Row>)
}

const CanvasUserViewContiner = ({
}) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ users: null, classes: [] });
    const [classEpendOwe, setClassExpendOwe] = useState(-1)
    const [canvasUserName, setCleverUsername] = useState('')
    const [userList, setUserList] = useState([])
    const [openModal, setOpenModal] = useState(false)
    const [userInfoLoader, setUserInfoLoader] = useState(-1)
    const [selectedUserCanvasInfo, setSelectedUserCanvasInfo] = useState({
        users: null, classes: []
    })
    const [workflowStateCourses, setWorkflowStateCourses] = useState({
        unpublished: [],
        completed: [],
        deleted: []
    })
    const [workflowStateLoading, setWorkflowStateLoading] = useState({
        unpublished: false,
        completed: false,
        deleted: false
    })
    const [expandedWorkflowSection, setExpandedWorkflowSection] = useState(null)
    const navigate = useNavigate()
    const handleChange = (panel) => (event, newExpanded) => {
        setClassExpendOwe(newExpanded ? panel : -1);
    };

    const fetchCleverUserByName = async () => {
        if (canvasUserName) {
            setLoading(true)
            const users = await handleUserByCanvasName(canvasUserName.trim())
            setUserList(users)
            setLoading(false)
        }

    }
    const fetchApis = async (id,auth_id) => {
        setUserInfoLoader(id)       
        setSelectedUserCanvasInfo({ users: null, classes: []})
        setWorkflowStateCourses({ unpublished: [], completed: [], deleted: [] })
        setExpandedWorkflowSection(null)
        const userData = await handleGetUserInfoById(id,auth_id)
        if(userData&&userData.user){
            setSelectedUserCanvasInfo(userData)
        }
        setUserInfoLoader(null)
        setOpenModal(true)
    }

    const fetchCoursesByWorkflowState = async (canvas_id, auth_id, workflowState) => {
        setWorkflowStateLoading(prev => ({ ...prev, [workflowState]: true }))
        try {
            const response = await handleGetCanvasCoursesByWorkflowState(canvas_id, auth_id, workflowState)
            if (response && response.success && response.data) {
                setWorkflowStateCourses(prev => ({ ...prev, [workflowState]: response.data }))
            }
        } catch (error) {
            console.error(`Error fetching ${workflowState} courses:`, error)
        } finally {
            setWorkflowStateLoading(prev => ({ ...prev, [workflowState]: false }))
        }
    }

    const handleWorkflowSectionChange = (section) => (event, isExpanded) => {
        setExpandedWorkflowSection(isExpanded ? section : null)
        if (isExpanded && workflowStateCourses[section].length === 0) {
            // Find the current user's canvas info to get auth_id
            const currentUser = userList.find(u => u.user.canvas_id === data.user?.canvas_id)
            if (currentUser?.user?.canvas_auth_id) {
                fetchCoursesByWorkflowState(currentUser.user.canvas_id, currentUser.user.canvas_auth_id, section)
            }
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        fetchCleverUserByName()

          };

      return (
        <SentryWrap>
            <Modal
                open={openModal}
                onClose={e => setOpenModal(false)}
                style={{ height: "100%", width: "100%" }}
            >
                <ModalContent>
                    <Row style={{ justifyContent: "flex-end", width: "100%" }}>
                        <IconButton
                            onClick={e => setOpenModal(false)}
                            style={{ padding: 0, marginRight: 10 }}
                        >
                            <Close color="secondary" />
                        </IconButton>
                    </Row>
                    <ModalSection>
                        <UserInfoAres data={data} />
                        <ClassInfoArea data={data} classEpendOwe={classEpendOwe} handleChange={handleChange} canvasInfo={selectedUserCanvasInfo} />
                        
                        {/* New Workflow State Sections - Positioned below Integration Classes */}
                        <Row style={{width:"100%", marginTop: "32px"}}>
                            <Col span={12} style={{paddingRight: "16px"}}>
                                {/* Empty column to maintain layout */}
                            </Col>
                            <Col span={12} style={{paddingLeft: "16px"}}>
                                <div style={{
                                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                    borderRadius: '12px',
                                    padding: '16px',
                                    marginBottom: '20px',
                                    border: '1px solid #dee2e6',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                    height: 'fit-content'
                                }}>
                                    <LabelTop style={{
                                        marginBottom: "16px", 
                                        textAlign: "center",
                                        fontSize: '16px',
                                        fontWeight: '700',
                                        color: '#495057',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Integration Courses by Status
                                    </LabelTop>
                                    
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {/* Unpublished Courses */}
                                        <div style={{
                                            background: '#fff',
                                            borderRadius: '8px',
                                            border: '1px solid #e9ecef',
                                            overflow: 'hidden',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                                        }}>
                                            <Accordion square expanded={expandedWorkflowSection === 'unpublished'} onChange={handleWorkflowSectionChange('unpublished')}>
                                                <AccordionSummary 
                                                    aria-controls="unpublished-content" 
                                                    id="unpublished-header"
                                                    style={{
                                                        background: 'linear-gradient(135deg, #17a2b8 0%, #138496 100%)',
                                                        color: 'white',
                                                        minHeight: '48px',
                                                        '&:hover': {
                                                            background: 'linear-gradient(135deg, #138496 0%, #117a8b 100%)'
                                                        }
                                                    }}
                                                >
                                                    <AccordianTitle style={{ color: 'white', fontWeight: '600', fontSize: '16px' }}>
                                                        <span>UNPUBLISHED COURSES</span>
                                                        <Rotate open={expandedWorkflowSection === 'unpublished'}>
                                                            <ArrowRight style={{ color: 'white' }} />
                                                        </Rotate>
                                                    </AccordianTitle>
                                                </AccordionSummary>
                                                <AccordionDetails style={{ padding: '0' }}>
                                                    {workflowStateLoading.unpublished ? (
                                                        <div style={{ padding: '20px' }}>
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                                                {/* Skeleton for course cards */}
                                                                {[1, 2, 3].map((index) => (
                                                                    <div key={index} style={{
                                                                        background: '#f8f9fa',
                                                                        borderRadius: '8px',
                                                                        padding: '16px',
                                                                        border: '1px solid #e9ecef'
                                                                    }}>
                                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                                                            <div style={{ flex: 1 }}>
                                                                                <Skeleton variant="text" width="80%" height={20} style={{ marginBottom: '8px' }} />
                                                                                <Skeleton variant="text" width="60%" height={16} />
                                                                            </div>
                                                                            <Skeleton variant="rectangular" width={80} height={24} style={{ borderRadius: '12px' }} />
                                                                        </div>
                                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                                                                            <div>
                                                                                <Skeleton variant="text" width="40%" height={14} style={{ marginBottom: '4px' }} />
                                                                                <Skeleton variant="text" width="70%" height={14} />
                                                                            </div>
                                                                            <div>
                                                                                <Skeleton variant="text" width="40%" height={14} style={{ marginBottom: '4px' }} />
                                                                                <Skeleton variant="text" width="70%" height={14} />
                                                                            </div>
                                                                        </div>
                                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                            <Skeleton variant="text" width="50%" height={14} />
                                                                            <Skeleton variant="text" width="30%" height={14} />
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <CourseInfoArea 
                                                            courses={workflowStateCourses.unpublished} 
                                                            loading={workflowStateLoading.unpublished} 
                                                            title="Unpublished Courses"
                                                            workflowState="unpublished"
                                                        />
                                                    )}
                                                </AccordionDetails>
                                            </Accordion>
                                        </div>

                                        {/* Completed Courses */}
                                        <div style={{
                                            background: '#fff',
                                            borderRadius: '8px',
                                            border: '1px solid #e9ecef',
                                            overflow: 'hidden',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                                        }}>
                                            <Accordion square expanded={expandedWorkflowSection === 'completed'} onChange={handleWorkflowSectionChange('completed')}>
                                                <AccordionSummary 
                                                    aria-controls="completed-content" 
                                                    id="completed-header"
                                                    style={{
                                                        background: 'linear-gradient(135deg, #28a745 0%, #1e7e34 100%)',
                                                        color: 'white',
                                                        minHeight: '48px',
                                                        '&:hover': {
                                                            background: 'linear-gradient(135deg, #1e7e34 0%, #155724 100%)'
                                                        }
                                                    }}
                                                >
                                                    <AccordianTitle style={{ color: 'white', fontWeight: '600', fontSize: '16px' }}>
                                                        <span>COMPLETED COURSES</span>
                                                        <Rotate open={expandedWorkflowSection === 'completed'}>
                                                            <ArrowRight style={{ color: 'white' }} />
                                                        </Rotate>
                                                    </AccordianTitle>
                                                </AccordionSummary>
                                                <AccordionDetails style={{ padding: '0' }}>
                                                    {workflowStateLoading.completed ? (
                                                        <div style={{ padding: '20px' }}>
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                                                {/* Skeleton for course cards */}
                                                                {[1, 2, 3].map((index) => (
                                                                    <div key={index} style={{
                                                                        background: '#f8f9fa',
                                                                        borderRadius: '8px',
                                                                        padding: '16px',
                                                                        border: '1px solid #e9ecef'
                                                                    }}>
                                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                                                            <div style={{ flex: 1 }}>
                                                                                <Skeleton variant="text" width="80%" height={20} style={{ marginBottom: '8px' }} />
                                                                                <Skeleton variant="text" width="60%" height={16} />
                                                                            </div>
                                                                            <Skeleton variant="rectangular" width={80} height={24} style={{ borderRadius: '12px' }} />
                                                                        </div>
                                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                                                                            <div>
                                                                                <Skeleton variant="text" width="40%" height={14} style={{ marginBottom: '4px' }} />
                                                                                <Skeleton variant="text" width="70%" height={14} />
                                                                            </div>
                                                                            <div>
                                                                                <Skeleton variant="text" width="40%" height={14} style={{ marginBottom: '4px' }} />
                                                                                <Skeleton variant="text" width="70%" height={14} />
                                                                            </div>
                                                                        </div>
                                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                            <Skeleton variant="text" width="50%" height={14} />
                                                                            <Skeleton variant="text" width="30%" height={14} />
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <CourseInfoArea 
                                                            courses={workflowStateCourses.completed} 
                                                            loading={workflowStateLoading.completed} 
                                                            title="Completed Courses"
                                                            workflowState="completed"
                                                        />
                                                    )}
                                                </AccordionDetails>
                                            </Accordion>
                                        </div>

                                        {/* Deleted Courses */}
                                        <div style={{
                                            background: '#fff',
                                            borderRadius: '8px',
                                            border: '1px solid #e9ecef',
                                            overflow: 'hidden',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                                        }}>
                                            <Accordion square expanded={expandedWorkflowSection === 'deleted'} onChange={handleWorkflowSectionChange('deleted')}>
                                                <AccordionSummary 
                                                    aria-controls="deleted-content" 
                                                    id="deleted-header"
                                                    style={{
                                                        background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                                                        color: 'white',
                                                        minHeight: '48px',
                                                        '&:hover': {
                                                            background: 'linear-gradient(135deg, #c82333 0%, #bd2130 100%)'
                                                        }
                                                    }}
                                                >
                                                    <AccordianTitle style={{ color: 'white', fontWeight: '600', fontSize: '16px' }}>
                                                        <span>DELETED COURSES</span>
                                                        <Rotate open={expandedWorkflowSection === 'deleted'}>
                                                            <ArrowRight style={{ color: 'white' }} />
                                                        </Rotate>
                                                    </AccordianTitle>
                                                </AccordionSummary>
                                                <AccordionDetails style={{ padding: '0' }}>
                                                    {workflowStateLoading.deleted ? (
                                                        <div style={{ padding: '20px' }}>
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                                                {/* Skeleton for course cards */}
                                                                {[1, 2, 3].map((index) => (
                                                                    <div key={index} style={{
                                                                        background: '#f8f9fa',
                                                                        borderRadius: '8px',
                                                                        padding: '16px',
                                                                        border: '1px solid #e9ecef'
                                                                    }}>
                                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                                                            <div style={{ flex: 1 }}>
                                                                                <Skeleton variant="text" width="80%" height={20} style={{ marginBottom: '8px' }} />
                                                                                <Skeleton variant="text" width="60%" height={16} />
                                                                            </div>
                                                                            <Skeleton variant="rectangular" width={80} height={24} style={{ borderRadius: '12px' }} />
                                                                        </div>
                                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                                                                            <div>
                                                                                <Skeleton variant="text" width="40%" height={14} style={{ marginBottom: '4px' }} />
                                                                                <Skeleton variant="text" width="70%" height={14} />
                                                                            </div>
                                                                            <div>
                                                                                <Skeleton variant="text" width="40%" height={14} style={{ marginBottom: '4px' }} />
                                                                                <Skeleton variant="text" width="70%" height={14} />
                                                                            </div>
                                                                        </div>
                                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                            <Skeleton variant="text" width="50%" height={14} />
                                                                            <Skeleton variant="text" width="30%" height={14} />
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <CourseInfoArea 
                                                            courses={workflowStateCourses.deleted} 
                                                            loading={workflowStateLoading.deleted} 
                                                            title="Deleted Courses"
                                                            workflowState="deleted"
                                                        />
                                                    )}
                                                </AccordionDetails>
                                            </Accordion>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </ModalSection>

                </ModalContent>
            </Modal>
            <SearchForm
                title="Canvas User Search"
                searchValue={canvasUserName}
                onSearchChange={e => setCleverUsername(e.target.value)}
                onSubmit={handleSubmit}
                loading={loading}
                showManageKeys={true}
                onManageKeysClick={e => {
                    e.preventDefault();
                    navigate("/keys?course=canvas")
                }}
            />
            <Wrapper>
                <LoadingWrapper loading={false}>
                    {userList.length > 0 ? <Table style={{ width: "100%", textAlign: "center" }}>
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ textAlign: "center" }}>Username</TableCell>
                                <TableCell style={{ textAlign: "center" }}>First name</TableCell>
                                <TableCell style={{ textAlign: "center" }}>Last Name</TableCell>
                                <TableCell style={{ textAlign: "center" }}>role</TableCell>
                                <TableCell style={{ textAlign: "center" }}>canvas id</TableCell>
                                <TableCell style={{ textAlign: "center" }}>Password</TableCell>
                                <TableCell style={{ textAlign: "center" }}>view</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {map(userList, u => {
                                const cuser = u.user;
                                return (<>
                                    <TableRow key={cuser.id}>
                                        <TableCell style={{ textAlign: "center" }}>{get(cuser, "username", '')}</TableCell>
                                        <TableCell style={{ textAlign: "center" }}>{get(cuser, "firstName", '')}</TableCell>
                                        <TableCell style={{ textAlign: "center" }}>{get(cuser, "lastName", '')}</TableCell>
                                        <TableCell style={{ textAlign: "center" }}>{get(cuser, "role", '')}</TableCell>
                                        <TableCell style={{ textAlign: "center" }}>{get(cuser, "canvas_id", '')}</TableCell>
                                        <TableCell style={{ textAlign: "center" }}><PasswordRevealInline userId={get(cuser, "id")} username={get(cuser, "username")} /></TableCell>
                                        <TableCell style={{ textAlign: "center" }}><IconButton onClick={() => {
                                            fetchApis(cuser.canvas_id,cuser.canvas_auth_id)
                                            setData(u)
                                        }}> {userInfoLoader ===cuser?.canvas_id?<Loading size={14}/>:<Visibility style={{ color: COLOR.green }} />}</IconButton></TableCell>
                                    </TableRow>
                                </>)
                            })}
                        </TableBody>
                    </Table> : null}
                </LoadingWrapper>
            </Wrapper>
        </SentryWrap>
    );
};


//  ----------------------------------------------------------------------------
//  Redux
//  ----------------------------------------------------------------------------
const mapStateToProps = state => ({
    logs: state.logs,
    currentUser: state.currentUser
});

export default connect(mapStateToProps, null)(CanvasUserViewContiner);
